import { useState } from 'react';
import { Container } from '@/components/Container';
import { BuscaPassagensWidget } from '@/components/BuscaPassagensWidget';
import { useTranslation } from '@/hooks/useTranslation';
import { useAnalytics } from '@/hooks/useAnalytics';
import { getWhatsAppUrl } from '@/data/loader';

/* ═══════════════════════════════════════════
   COMO CHEGAR — A jornada completa
   Dados reais + pesquisa de concorrentes
   ═══════════════════════════════════════════ */

type Origin = 'curitiba' | 'outros';
type Transport = 'carro' | 'onibus' | 'aviao';

interface PlanEstimate {
  time: string;
  costRange: string;
  details: string[];
}

const planEstimates: Record<Origin, Partial<Record<Transport, PlanEstimate>>> = {
  curitiba: {
    carro: {
      time: '~3h30',
      costRange: 'R$ 150–280',
      details: [
        'Pedágio BR-277: ~R$ 50 (ida)',
        'Gasolina ~200km: ~R$ 100–150',
        'Estacionamento Pontal do Sul: R$ 40–80/dia',
      ],
    },
    onibus: {
      time: '~3h30',
      costRange: 'R$ 50–80',
      details: [
        'Saída: Terminal Rodoviário de Curitiba',
        'Destino: Pontal do Sul ou Paranaguá',
        'Empresas: Viação Salineira / Colitur',
      ],
    },
  },
  outros: {
    carro: {
      time: '4–8h (varia pela origem)',
      costRange: 'R$ 200–600',
      details: [
        'Via BR-277 até Pontal do Sul (Curitiba) ou Paranaguá',
        'Consulte combustível + pedágio da sua cidade de origem',
      ],
    },
    onibus: {
      time: '4–14h (varia)',
      costRange: 'R$ 80–350',
      details: [
        'Conexões de SP, PR interior, SC e RS',
        'Destino final: Paranaguá ou Pontal do Sul',
      ],
    },
    aviao: {
      time: '~6h (total porta a porta)',
      costRange: 'R$ 400–1.200',
      details: [
        'Voo até Curitiba (CWB) — sem voos diretos para Paranaguá',
        'Do aeroporto: ônibus ou carro até o embarque (~1h40)',
      ],
    },
  },
};

type Season = 'alta' | 'media' | 'baixa';

interface ScheduleSlot {
  saida: string;
  obs?: string;
  isLastDeparture?: boolean;
}

const horariosPorTemporada: Record<Season, ScheduleSlot[]> = {
  alta: [
    { saida: '07:00' },
    { saida: '08:00' },
    { saida: '09:30' },
    { saida: '11:00' },
    { saida: '13:00' },
    { saida: '15:00' },
    { saida: '17:00', obs: 'Última saída (maioria dos dias)', isLastDeparture: true },
    { saida: '19:00', obs: 'Apenas jan/fev e feriados prolongados' },
  ],
  media: [
    { saida: '08:00' },
    { saida: '09:30' },
    { saida: '11:00' },
    { saida: '13:00' },
    { saida: '15:00' },
    { saida: '17:00', obs: 'Última saída', isLastDeparture: true },
  ],
  baixa: [
    { saida: '09:30' },
    { saida: '11:00' },
    { saida: '13:00' },
    { saida: '15:00' },
    { saida: '17:00', obs: 'Última saída — atenção!', isLastDeparture: true },
  ],
};

const passos = [
  {
    num: '1',
    icon: '✈️',
    detalhes: ['guide.gettingHere.step1.detail1', 'guide.gettingHere.step1.detail2'],
  },
  {
    num: '2',
    icon: '🚌',
    detalhes: [
      'guide.gettingHere.step2.detail1',
      'guide.gettingHere.step2.detail2',
      'guide.gettingHere.step2.detail3',
      'guide.gettingHere.step2.detail4',
    ],
  },
  {
    num: '3',
    icon: '🅿️',
    detalhes: [
      'guide.gettingHere.step3.detail1',
      'guide.gettingHere.step3.detail2',
      'guide.gettingHere.step3.detail3',
    ],
  },
  {
    num: '4',
    icon: '🎫',
    detalhes: [
      'guide.gettingHere.step4.detail1',
      'guide.gettingHere.step4.detail2',
      'guide.gettingHere.step4.detail3',
      'guide.gettingHere.step4.detail4',
    ],
  },
  {
    num: '5',
    icon: '⛵',
    detalhes: [
      'guide.gettingHere.step5.detail1',
      'guide.gettingHere.step5.detail2',
      'guide.gettingHere.step5.detail3',
    ],
  },
];

const opcoes = [
  {
    id: 'pontal',
    cor: 'bg-teal-50 border-teal-200',
    tagCor: 'bg-teal-deep text-white',
    tempo: '30–35 min',
    preco: 'R$44,18',
    frequencia: 'guide.gettingHere.option.pontal.frequencia',
    horario: 'guide.gettingHere.option.pontal.horario',
    vantagens: [
      'guide.gettingHere.option.pontal.vantagem1',
      'guide.gettingHere.option.pontal.vantagem2',
      'guide.gettingHere.option.pontal.vantagem3',
      'guide.gettingHere.option.pontal.vantagem4',
    ],
  },
  {
    id: 'paranagua',
    cor: 'bg-sand-50 border-sand-200',
    tagCor: 'bg-warm-gray text-white',
    tempo: '1h45',
    preco: 'R$76,50',
    frequencia: 'guide.gettingHere.option.paranagua.frequencia',
    horario: 'guide.gettingHere.option.paranagua.horario',
    vantagens: [
      'guide.gettingHere.option.paranagua.vantagem1',
      'guide.gettingHere.option.paranagua.vantagem2',
      'guide.gettingHere.option.paranagua.vantagem3',
    ],
  },
];

const howToSchema = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'Como chegar na Ilha do Mel',
  description:
    'Guia passo a passo completo para chegar na Ilha do Mel saindo de Curitiba ou de qualquer cidade do Brasil',
  step: [
    {
      '@type': 'HowToStep',
      position: 1,
      name: 'Chegue a Curitiba ou Paranaguá',
      text: 'O primeiro passo é chegar até o litoral do Paraná. De Curitiba, são ~130km pela BR-277 (1h40 de carro).',
    },
    {
      '@type': 'HowToStep',
      position: 2,
      name: 'Vá até Pontal do Sul ou Paranaguá',
      text: 'De carro ou ônibus até o porto de embarque. Pontal do Sul é mais próximo de Curitiba e mais recomendado.',
    },
    {
      '@type': 'HowToStep',
      position: 3,
      name: 'Estacione o veículo (se vier de carro)',
      text: 'Deixe o carro nos estacionamentos pagos próximos ao trapiche em Pontal do Sul (~R$50–80/dia).',
    },
    {
      '@type': 'HowToStep',
      position: 4,
      name: 'Compre o bilhete da balsa',
      text: 'Compre a passagem de barco no guichê ou online. Ida e volta: R$44,18 saindo de Pontal do Sul.',
    },
    {
      '@type': 'HowToStep',
      position: 5,
      name: 'Embarque e atravesse a baía',
      text: 'A travessia de Pontal do Sul leva 30–35 minutos até a Ilha do Mel.',
    },
  ],
};

const whatsappSvg = (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export function ComoChegarSection() {
  const { t } = useTranslation();
  const { trackWhatsapp } = useAnalytics();

  const [origin, setOrigin] = useState<Origin>('curitiba');
  const [transport, setTransport] = useState<Transport>('carro');
  const [season, setSeason] = useState<Season>('media');

  const handleOriginChange = (newOrigin: Origin) => {
    setOrigin(newOrigin);
    if (newOrigin === 'curitiba' && transport === 'aviao') {
      setTransport('carro');
    }
  };

  const currentEstimate = planEstimates[origin][transport];
  const horarios = horariosPorTemporada[season];

  return (
    <section id="como-chegar" className="py-16 sm:py-20 bg-sand-50">
      {/* HowTo Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }}
      />

      <Container>
        {/* Header */}
        <div className="text-center mb-12">
          <span className="font-body text-sm text-coral font-semibold uppercase tracking-wider">
            {t('guide.gettingHere.overline')}
          </span>
          <h2 className="font-display text-4xl sm:text-5xl text-warm-dark mt-2">
            {t('guide.gettingHere.title')}
          </h2>
          <p className="font-editorial text-lg text-warm-gray mt-2 max-w-2xl mx-auto">
            {t('guide.gettingHere.description')}
          </p>
        </div>

        {/* Timeline */}
        <div className="max-w-3xl mx-auto mb-16">
          <div className="relative">
            {/* Linha vertical */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-sand-200 hidden sm:block" />

            <div className="space-y-8">
              {passos.map(p => (
                <div key={p.num} className="relative flex gap-4 sm:gap-6">
                  {/* Círculo numerado */}
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-teal-deep text-white flex items-center justify-center font-display text-xl z-10">
                    {p.num}
                  </div>

                  {/* Conteúdo */}
                  <div className="flex-1 bg-white rounded-xl border border-sand-200 p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                    <div className="flex items-center gap-2 mb-2">
                      <span
                        role="img"
                        aria-label={t(`guide.gettingHere.step${p.num}.title`)}
                        className="text-xl"
                      >
                        {p.icon}
                      </span>
                      <h3 className="font-display text-lg text-warm-dark">
                        {t(`guide.gettingHere.step${p.num}.title`)}
                      </h3>
                    </div>
                    <p className="font-body text-sm text-warm-gray mb-3">
                      {t(`guide.gettingHere.step${p.num}.desc`)}
                    </p>
                    <ul className="space-y-1.5">
                      {p.detalhes.map(d => {
                        const text = t(d);
                        return (
                          <li key={d} className="flex items-start gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-teal-400 mt-2 flex-shrink-0" />
                            <span
                              className={`font-body text-sm ${text.startsWith('⚠️') ? 'text-coral font-medium' : 'text-warm-gray'}`}
                            >
                              {text}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Planejador interativo de rota ── */}
        <div className="max-w-2xl mx-auto mb-10">
          <h3 className="font-display text-xl text-warm-dark mb-5 text-center">
            Planeje sua rota até a ilha
          </h3>

          {/* Seletor de origem */}
          <div className="mb-4">
            <p className="font-body text-xs font-semibold text-warm-gray uppercase tracking-wider mb-2">
              Saindo de
            </p>
            <div className="flex gap-2 flex-wrap">
              {(['curitiba', 'outros'] as Origin[]).map(o => (
                <button
                  key={o}
                  aria-pressed={origin === o}
                  onClick={() => handleOriginChange(o)}
                  className={`px-4 py-2 rounded-full font-body text-sm font-semibold transition-all min-h-[44px] ${
                    origin === o
                      ? 'bg-teal-deep text-white shadow-sm'
                      : 'bg-white border border-sand-200 text-warm-gray hover:border-teal-300'
                  }`}
                >
                  {o === 'curitiba' ? 'Curitiba' : 'Outras cidades'}
                </button>
              ))}
            </div>
          </div>

          {/* Seletor de transporte */}
          <div className="mb-5">
            <p className="font-body text-xs font-semibold text-warm-gray uppercase tracking-wider mb-2">
              Transporte
            </p>
            <div className="flex gap-2 flex-wrap">
              {(
                [
                  { id: 'carro', label: '🚗 Carro' },
                  { id: 'onibus', label: '🚌 Ônibus' },
                  { id: 'aviao', label: '✈️ Avião' },
                ] as { id: Transport; label: string }[]
              ).map(tr => {
                const isDisabled = origin === 'curitiba' && tr.id === 'aviao';
                return (
                  <button
                    key={tr.id}
                    aria-pressed={transport === tr.id}
                    disabled={isDisabled}
                    onClick={() => !isDisabled && setTransport(tr.id)}
                    className={`px-4 py-2 rounded-full font-body text-sm font-semibold transition-all min-h-[44px] ${
                      isDisabled
                        ? 'opacity-40 cursor-not-allowed bg-white border border-sand-200 text-warm-gray'
                        : transport === tr.id
                          ? 'bg-teal-deep text-white shadow-sm'
                          : 'bg-white border border-sand-200 text-warm-gray hover:border-teal-300'
                    }`}
                  >
                    {tr.label}
                  </button>
                );
              })}
            </div>
            {origin === 'curitiba' && (
              <p className="font-body text-xs text-warm-gray mt-1.5">
                ✈️ Avião não disponível saindo de Curitiba (aeroporto local)
              </p>
            )}
          </div>

          {/* Card de resultado */}
          <div aria-live="polite" className="min-h-[140px]">
            {currentEstimate ? (
              <div className="bg-white rounded-xl border border-sand-200 p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                <div className="flex gap-4 mb-4">
                  <div className="flex-1 bg-teal-50 rounded-lg p-3 text-center">
                    <div className="font-display text-xl text-teal-deep">
                      {currentEstimate.time}
                    </div>
                    <div className="font-body text-xs text-warm-gray">Tempo estimado</div>
                  </div>
                  <div className="flex-1 bg-sand-50 rounded-lg p-3 text-center">
                    <div className="font-display text-xl text-coral">
                      {currentEstimate.costRange}
                    </div>
                    <div className="font-body text-xs text-warm-gray">Custo estimado</div>
                  </div>
                </div>
                <ul className="space-y-1.5">
                  {currentEstimate.details.map((d, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-teal-400 mt-2 flex-shrink-0" />
                      <span className="font-body text-sm text-warm-gray">{d}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </div>

        {/* Cards comparativos */}
        <div className="grid md:grid-cols-2 gap-5 mb-16">
          {opcoes.map(o => (
            <div key={o.id} className={`rounded-2xl border-2 p-6 ${o.cor}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-display text-2xl text-warm-dark">
                    {t(`guide.gettingHere.option.${o.id}.name`).toUpperCase()}
                  </h3>
                  {o.id === 'pontal' && (
                    <span className="bg-teal-deep text-white text-xs font-body font-bold px-2 py-0.5 rounded-full">
                      Recomendado
                    </span>
                  )}
                </div>
                <span className={`${o.tagCor} text-xs font-body font-bold px-3 py-1 rounded-full`}>
                  {t(`guide.gettingHere.option.${o.id}.tag`)}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="bg-white rounded-lg p-3 text-center border border-sand-100">
                  <div className="font-display text-xl text-teal-deep">{o.tempo}</div>
                  <div className="font-body text-xs text-warm-gray">
                    {t('guide.gettingHere.labels.crossing')}
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 text-center border border-sand-100">
                  <div className="font-display text-xl text-coral">{o.preco}</div>
                  <div className="font-body text-xs text-warm-gray">
                    {t('guide.gettingHere.labels.roundTrip')}
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 text-center border border-sand-100">
                  <div className="font-display text-xl text-warm-dark">{t(o.frequencia)}</div>
                  <div className="font-body text-xs text-warm-gray">
                    {t('guide.gettingHere.labels.frequency')}
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 text-center border border-sand-100">
                  <div className="font-display text-xl text-warm-dark">{t(o.horario)}</div>
                  <div className="font-body text-xs text-warm-gray">
                    {t('guide.gettingHere.labels.hours')}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                {o.vantagens.map(v => (
                  <div key={v} className="flex items-start gap-2">
                    <svg
                      className="w-4 h-4 text-teal-deep mt-0.5 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="font-body text-sm text-warm-gray">{t(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Tabela de horários Pontal do Sul — com tabs sazonais */}
        <div className="mb-12">
          <h3 className="font-display text-xl text-warm-dark mb-4 text-center">
            {t('guide.gettingHere.scheduleTitle')}
          </h3>

          {/* Tabs de temporada */}
          <div className="max-w-lg mx-auto">
            <div role="tablist" className="flex gap-2 mb-4 flex-wrap">
              {(['alta', 'media', 'baixa'] as Season[]).map(s => (
                <button
                  key={s}
                  role="tab"
                  aria-selected={season === s}
                  onClick={() => setSeason(s)}
                  className={`px-3 py-1.5 rounded-full font-body text-xs font-semibold transition-all min-h-[44px] ${
                    season === s
                      ? 'bg-teal-deep text-white'
                      : 'bg-white border border-sand-200 text-warm-gray hover:border-teal-300'
                  }`}
                >
                  {s === 'alta'
                    ? 'Alta (jan–mar, jul, dez)'
                    : s === 'media'
                      ? 'Média (abr, jun, ago–set)'
                      : 'Baixa (mai, out–nov)'}
                </button>
              ))}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full font-body text-sm">
                <thead>
                  <tr className="border-b-2 border-sand-200">
                    <th className="text-left py-2 px-4 font-semibold text-warm-dark">
                      {t('guide.gettingHere.scheduleHeaderDeparture')}
                    </th>
                    <th className="text-left py-2 px-4 font-semibold text-warm-dark">
                      {t('guide.gettingHere.scheduleHeaderNote')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {horarios.map(h => (
                    <tr
                      key={h.saida}
                      className={`border-b border-sand-100 ${h.isLastDeparture ? 'bg-coral/5' : ''}`}
                    >
                      <td
                        className={`py-2 px-4 font-display ${h.isLastDeparture ? 'text-coral font-semibold' : 'text-warm-dark'}`}
                      >
                        {h.isLastDeparture && <span className="mr-1">•</span>}
                        {h.saida}
                      </td>
                      <td className="py-2 px-4 text-warm-gray">
                        {h.obs ?? t('guide.gettingHere.scheduleDaily')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="font-body text-xs text-warm-gray text-center mt-3">
            {t('guide.gettingHere.scheduleNote')}
          </p>
        </div>

        {/* Alerta de compra antecipada */}
        <div className="max-w-lg mx-auto">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3 mb-4">
            <span role="img" aria-label="Atenção" className="text-xl flex-shrink-0">
              ⏰
            </span>
            <div>
              <p className="font-body text-sm font-semibold text-warm-dark">
                Compre sua passagem com antecedência
              </p>
              <p className="font-body text-sm text-warm-gray">
                Na alta temporada os horários lotam rápido. Reserve com alguns dias de antecedência
                para garantir seu embarque.
              </p>
            </div>
          </div>
        </div>

        {/* Widget de passagens */}
        <div className="max-w-lg mx-auto mb-12">
          <BuscaPassagensWidget />
        </div>

        {/* Alertas finais */}
        <div className="space-y-4 max-w-2xl mx-auto">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
            <span role="img" aria-label="Aviso" className="text-2xl">
              ⚠️
            </span>
            <div>
              <p className="font-body text-sm font-semibold text-warm-dark">
                {t('guide.gettingHere.alert.pierTrap.title')}
              </p>
              <p className="font-body text-sm text-warm-gray">
                {t('guide.gettingHere.alert.pierTrap.desc')}
              </p>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
            <span role="img" aria-label="Dica" className="text-2xl">
              💡
            </span>
            <div>
              <p className="font-body text-sm font-semibold text-warm-dark">
                {t('guide.gettingHere.alert.baggage.title')}
              </p>
              <p className="font-body text-sm text-warm-gray">
                {t('guide.gettingHere.alert.baggage.desc')}
              </p>
            </div>
          </div>

          <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 flex gap-3">
            <span role="img" aria-label="Remédio para cinetose" className="text-2xl">
              💊
            </span>
            <div>
              <p className="font-body text-sm font-semibold text-warm-dark">
                {t('guide.gettingHere.alert.seasickness.title')}
              </p>
              <p className="font-body text-sm text-warm-gray">
                {t('guide.gettingHere.alert.seasickness.desc')}
              </p>
            </div>
          </div>
        </div>

        {/* WhatsApp CTA */}
        <div className="mt-10 text-center">
          <p className="font-body text-sm text-warm-gray mb-3">
            Tem dúvida sobre a rota ou quer ajuda para planejar?
          </p>
          <a
            href={getWhatsAppUrl(
              '41999999999',
              'Oi! Li o guia de como chegar na Ilha do Mel e tenho uma dúvida sobre a rota:',
              'guia-como-chegar-cta'
            )}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackWhatsapp({ provider: 'Geral', source: 'guia-como-chegar-cta' })}
            className="inline-flex items-center gap-2.5 bg-[#25D366] hover:bg-[#20bd5a] text-white font-body font-semibold text-sm rounded-xl px-6 py-3 min-h-[44px] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
          >
            {whatsappSvg}
            Fala com a gente no WhatsApp
          </a>
        </div>
      </Container>
    </section>
  );
}
